import React, {useContext, useEffect, useMemo, useState} from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	IconButton,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
	useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";

import {useSSTranslation} from "@/i18n/useSSTranslation";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import CustomAutocomplete from "@/components/common/CustomAutocomplete";
import LogoSelector from "@/components/common/LogoSelector";
import RegexTextField from "@/components/common/RegexTextField";

import useCertificationsOrganizationGrantingEntities from "@/hooks/useCertificationsOrganizationGrantingEntities";

import {
	AlphanumericAmpHyphenApostropheParenthesesColonRegex,
	EnglishAlphabetAndNumberRegex,
	onlyNumbers
} from "@/constants";

import {
	CertificationResponseDto,
	DurationUnit
} from "@buildwitt/identity-client";

import {
	INotificationSeverity,
	NotificationContext,
	useApi
} from "@buildwitt/shapeshift-identity";

import {buttonSx} from "@/pages/ManagerView/Pages/Users/BulkReschedule/BulkRescheduleConfirmationModal";

const inputLabelStyle = {shrink: true, sx: {color: "#aaa"}};

export default function AddCertificationModal({open, defaultName, onClose, onSave}: {
	open: boolean;
	defaultName: string;
	onClose: () => void;
	onSave: (cert: { id: string; label: string }) => void;
}) {
	const [fields, setFields] = useState({
		name: defaultName,
		code: "",
		organization: "",
		expirationEnabled: false,
		expirationNumber: "",
		expirationUnit: "",
		description: "",
		logo: null as File | null
	});
	const theme = useTheme();
	const [touched, setTouched] = useState(false);
	const [selectedGrantingEntity, setSelectedGrantingEntity] = useState<{ id: string; label: string } | null>(null);

	const {notify} = useContext(NotificationContext);
	const [pageBaseState] = useContext(PageBaseContext);
	const {t} = useSSTranslation();

	const {grantingEntities, handleSearchChange, loadMoreGrantingEntities, hasNextPage, apiResult: certificationsApiResult} =
		useCertificationsOrganizationGrantingEntities();

	const [submitResponse, submitApi] = useApi<CertificationResponseDto>();

	const cohortId = useMemo(
		() => (pageBaseState?.ownedCohort && typeof pageBaseState.ownedCohort === "object" ? pageBaseState.ownedCohort.id : null),
		[pageBaseState]
	);

	useEffect(() => setFields((prev) => ({...prev, name: defaultName})), [defaultName]);
	useEffect(() => handleSearchChange(""), []);

	const handleChange = (field: string, value: any) => {
		setFields((prev) => ({...prev, [field]: value}));
	};

	const handleFileChange = (logo: File) => {
		if (logo) handleChange("logo", logo);
	};

	const {name, code, organization, expirationEnabled, expirationNumber, expirationUnit, description, logo} = fields;

	const isFormValid =
		name.trim() &&
		organization.trim() &&
		(!expirationEnabled || (expirationNumber.trim() && expirationUnit.trim()));

	const handleSave = () => {
		setTouched(true);
		if (!isFormValid) return;

		const formData = new FormData();
		formData.append(
			"certificationPostRequestDto",
			JSON.stringify({
				code: code.trim() || null,
				description: description.trim() || null,
				grantingEntityId: organization.trim(),
				durationPeriod: expirationEnabled ? parseInt(expirationNumber.trim(), 10) : null,
				durationUnit: expirationEnabled ? expirationUnit : null,
				name: name.trim()
			})
		);
		if (logo) formData.append("logoToUpload", logo);

		submitApi({
			url: `/api/cohort/${cohortId}/organization/certifications`,
			method: "POST",
			data: formData,
			config: {headers: {"Content-Type": "multipart/form-data"}}
		});
	};

	useEffect(() => {
		if (submitResponse.statusCode === undefined) return;

		if (submitResponse.statusCode === 200) {
			notify(
				t("playlist_save_success", "Information saved successfully"),
				"",
				INotificationSeverity.success,
				5000
			);
			onSave({
				id: submitResponse.data.id,
				label: submitResponse.data.name
			});
		} else {
			notify(
				t("playlist_save_error", "Unable to save information. Please try again."),
				"",
				INotificationSeverity.error,
				5000
			);
		}
	}, [submitResponse.statusCode]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{sx: {backgroundColor: theme.palette.background.paperElevation2, color: "#fff", borderRadius: 2}}}
		>
			<DialogTitle>
				{t("certification_add", "Add Certification")}
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{position: "absolute", right: 8, top: 8, color: "#fff"}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{pt: "24px !important", display: "flex", flexDirection: "column", gap: 2}}>
				<RegexTextField
					regex={AlphanumericAmpHyphenApostropheParenthesesColonRegex}
					autoFocus
					label={t("certification_name", "Certification Name")}
					fullWidth
					required
					inputProps={{maxLength: 250}}
					value={name}
					onChange={(e) => handleChange("name", e.target.value)}
					error={touched && !name.trim()}
					helperText={touched && !name.trim() ? t("common_Required", "Required field") : ""}
					InputLabelProps={inputLabelStyle}
				/>

				<Box display="flex" gap={2}>
					<RegexTextField
						regex={EnglishAlphabetAndNumberRegex}
						label={t("certification_code", "Certification Code")}
						fullWidth
						inputProps={{maxLength: 8}}
						value={code}
						onChange={(e) => handleChange("code", e.target.value)}
						InputLabelProps={inputLabelStyle}
					/>

					<FormControl fullWidth required error={touched && !organization.trim()}>
						<CustomAutocomplete
							required
							label={t("certification_granting_organization_name", "Granting Organization Name")}
							options={grantingEntities}
							value={selectedGrantingEntity}
							onChange={(_, newValue) => {
								handleChange("organization", newValue.id);
								setSelectedGrantingEntity(newValue);
							}}
							onInputChange={(_, newInputValue, reason) => {
								if (reason !== "reset") handleSearchChange(newInputValue.trim());
							}}
							loading={certificationsApiResult.isLoading}
							hasNextPage={hasNextPage}
							isFetching={certificationsApiResult.isLoading}
							loadMoreOptions={loadMoreGrantingEntities}
							apiResult={certificationsApiResult}
						/>
						{touched && !organization.trim() && (
							<Typography variant="caption" color="error" sx={{ml: 2, mt: 0.5}}>
								{t("common_Required", "Required field")}
							</Typography>
						)}
					</FormControl>
				</Box>

				<FormControlLabel
					control={
						<Switch
							checked={expirationEnabled}
							onChange={(e) => handleChange("expirationEnabled", e.target.checked)}
						/>
					}
					label={t("certification_expiration", "Certification Expiration")}
				/>

				{expirationEnabled && (
					<Box display="flex" gap={2}>
						<RegexTextField
							regex={onlyNumbers}
							inputProps={{maxLength: 8}}
							label={t("certification_enter_number", "Enter Number")}
							fullWidth
							required
							value={expirationNumber}
							onChange={(e) => handleChange("expirationNumber", e.target.value)}
							error={touched && !expirationNumber.trim()}
							helperText={touched && !expirationNumber.trim() ? t("common_Required", "Required field") : ""}
							InputLabelProps={inputLabelStyle}
						/>
						<FormControl fullWidth required error={touched && !expirationUnit.trim()}>
							<InputLabel {...inputLabelStyle}>
								{t("certification_select_expirationUnit", "Select Expiration Unit")}
							</InputLabel>
							<Select
								value={expirationUnit}
								onChange={(e) => handleChange("expirationUnit", e.target.value)}
							>
								{Object.values(DurationUnit).map((unit) => (
									<MenuItem key={unit} value={unit}>
										{unit}
									</MenuItem>
								))}
							</Select>
							{touched && !expirationUnit.trim() && (
								<Typography variant="caption" color="error" sx={{ml: 2, mt: 0.5}}>
									{t("common_Required", "Required field")}
								</Typography>
							)}
						</FormControl>
					</Box>
				)}

				<TextField
					label={t("common_description", "Description")}
					fullWidth
					multiline
					rows={4}
					inputProps={{maxLength: 500}}
					value={description}
					onChange={(e) => handleChange("description", e.target.value)}
					InputLabelProps={inputLabelStyle}
				/>

				<Box
					sx={{
						textAlign: "center"
					}}
				>
					<LogoSelector
						label={t("certification_logo", "Certification Logo")}
						emptyLabel={t("certification_empty_logo", "Upload Certification Logo")}
						onImageSelected={(logo?: File) => handleFileChange(logo)}
					/>
				</Box>
			</DialogContent>

			<DialogActions sx={{p: 2}}>
				<Button onClick={onClose} sx={buttonSx}>
					{t("common_notes_cancel", "Cancel")}
				</Button>
				<LoadingButton loading={submitResponse.isLoading} variant="text" onClick={handleSave} sx={buttonSx}>
					{t("common_confirm", "Confirm")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
